import type { CSSProperties } from 'react';
import { MapPin } from 'lucide-react';
import { useI18n } from '@nx/i18n';
import { getProfilePublicPathPrefix } from '@nx/shared';
import { APP_BASE_PATH } from '../../config/appBasePath';
import { getPartnerRoleDisplay } from '../../constants/partnerRoleDisplay';
import { ProfileAvatar } from './ProfileAvatar';
import { ProfileComponentBlock } from './ProfileComponentBlock';
import { safeCssColor } from '../../utils/safeCssColor';
import { cssUrlValue, safeCssBackgroundImageUrl } from '../../utils/safeCssBackgroundImageUrl';
import type { ProfileComponentViewModel, ProfileViewModel } from './profileViewModel';
import './public-profile.css';

interface PublicProfileProps {
  profile: ProfileViewModel;
  onComponentClick?: (comp: ProfileComponentViewModel) => void;
}

export function PublicProfile({ profile, onComponentClick }: PublicProfileProps) {
  const { t } = useI18n();
  const theme = profile.themeId;
  const accent = profile.accent || '#6B7A5A';
  const visible = profile.components.filter((c) => c.visible !== false);
  const roleText = profile.role.trim();
  const locationText = profile.location?.trim() ?? '';
  const roleDisplay = getPartnerRoleDisplay(profile.roleType);
  const RoleIcon = roleDisplay.icon;
  const roleLabel = t(roleDisplay.profileLabelKey);

  const safeBgUrl = safeCssBackgroundImageUrl(profile.backgroundImageUrl);
  const hasCustomBackground = Boolean(safeBgUrl);
  const wrapClass = `pp pp--${theme}${hasCustomBackground ? ' pp--custom-bg' : ''}${theme === 'costiera' && !hasCustomBackground ? ' pp--costiera-bg' : ''}`;

  const accentStyle = {
    '--accent': accent,
    ...(safeBgUrl
      ? {
          '--pp-bg-image': cssUrlValue(safeBgUrl),
          '--pp-bg-color': safeCssColor(profile.backgroundColor) ?? '#0a1218',
        }
      : {}),
  } as CSSProperties;

  return (
    <div className={wrapClass} style={accentStyle}>
      <div className="pp-inner">
        <header className="pp-head">
          <ProfileAvatar
            name={profile.name}
            initials={profile.initials}
            color={accent}
            size={theme === 'terra' ? 84 : 88}
            square={theme === 'terra'}
            imageUrl={profile.avatarUrl}
          />
          <span className="pp-identity">
            <RoleIcon className="h-3 w-3" strokeWidth={2} />
            {roleLabel}
          </span>
          <h1 className="pp-name">{profile.name}</h1>
          {roleText ? <p className="pp-role">{roleText}</p> : null}
          {profile.bio ? <p className="pp-bio">{profile.bio}</p> : null}
          {locationText ? (
            <p className="pp-loc">
              <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
              {locationText}
            </p>
          ) : null}
        </header>

        <div className="pp-stack">
          {visible.map((c) => (
            <ProfileComponentBlock
              key={c.id}
              comp={c}
              accent={accent}
              onClick={onComponentClick}
            />
          ))}
        </div>

        <footer className="pp-foot">
          <span className="pp-foot-mark">{t('profile.public.footerMark')}</span>
          <span className="pp-foot-sub">
            {getProfilePublicPathPrefix(APP_BASE_PATH)}
            {profile.slug}
          </span>
        </footer>
      </div>
    </div>
  );
}
